import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';
import { getErrorMessage } from '@common/utils';
import { useGetTranslationSummary } from '@/reactQuery/get';
import { useMutateGenerateTranscriptsTranslation } from '@/reactQuery/post';
import useTranslationsLoadingStore from '@/store/TranslationLoadingStore';
import useTranslation from '@/hooks/useTranslation';

/**
 * TELV1-501 — coverage banner + self-heal for transcript translations.
 *
 * The translated list view is a union of native-target-language originals
 * and translated documents, so a conversation whose translation failed
 * silently DROPS OUT of the list — the view looks 100% translated while
 * the export shows untranslated cells (the NIQ incident). This banner
 * makes the gap visible ("translated X / Y") and offers a forced retry
 * that bypasses the server's DLQ-attempts cap and enqueue cooldown.
 *
 * Self-heal: on first mount per project per session, if any existing
 * language has untranslated conversations, ONE non-forced re-trigger is
 * fired. The server enforces an enqueue cooldown and a DLQ-attempts cap,
 * so repeated mounts/tabs/users cannot stack duplicate work or burn
 * tokens on poison conversations — this client guard is only the first
 * line of defense.
 */
const selfHealFiredForProject = new Set<string>();

interface Props {
  projectId?: string;
  /** Currently viewed translation language (null = Original view). */
  translationLanguage: string | null;
  conversationCount?: number;
}

function TranslationCoverageBanner({ projectId, translationLanguage, conversationCount }: Props) {
  const { lang } = useTranslation();
  const { data: summary } = useGetTranslationSummary(projectId);
  const { mutateAsync: triggerTranslation } = useMutateGenerateTranscriptsTranslation(projectId);
  const { translatingProjects, setTranslatingProjects } = useTranslationsLoadingStore();

  const isTranslating = !!translatingProjects?.find((p) => p.projectId === projectId);

  // Self-heal: one non-forced catch-up per project per session. Server-side
  // cooldown + attempts cap make this a cheap no-op when nothing is missing.
  useEffect(() => {
    if (!projectId || !summary || selfHealFiredForProject.has(projectId)) return;
    const gapLanguages = summary
      .filter((entry) => entry.translated < entry.total)
      .map((entry) => entry.language);
    if (gapLanguages.length === 0) return;
    // Add BEFORE the call so a summary refetch mid-flight can't double-
    // fire; remove on failure so the next mount retries (PR review #6 —
    // a transient network error must not disable the self-heal for the
    // whole session).
    selfHealFiredForProject.add(projectId);
    triggerTranslation({ languages: gapLanguages }).catch(() => {
      // Otherwise silent: this is an opportunistic catch-up. The banner +
      // manual retry remain available; server logs/Sentry carry the failure.
      selfHealFiredForProject.delete(projectId);
    });
  }, [projectId, summary, triggerTranslation]);

  if (!projectId || !translationLanguage || isTranslating) return null;

  const entry = summary?.find((s) => s.language === translationLanguage);
  if (!entry || entry.translated >= entry.total) return null;

  const handleRetry = async () => {
    setTranslatingProjects((prev) => [
      ...(prev || []),
      {
        projectId,
        language: translationLanguage,
        length: conversationCount ?? entry.total,
        source: 'transcripts',
        // Partial retry: completion detection must see the count EXCEED
        // this baseline before a stable plateau counts as done — see
        // TranslationLoadingStore.baselineCount (TELV1-501 audit #2).
        baselineCount: entry.translated,
      },
    ]);
    try {
      await triggerTranslation({ languages: [translationLanguage], force: true });
      toast.info(`${lang.get('msg.retryMissingTranslationsQueued')} (${translationLanguage})`);
    } catch (error) {
      toast.error(getErrorMessage(error));
      setTranslatingProjects(
        (prev) =>
          prev?.filter((p) => !(p.projectId === projectId && p.source === 'transcripts')) || [],
      );
    }
  };

  return (
    <div
      data-testid="translation-coverage-banner"
      className="flex items-center justify-between gap-3 px-4 py-2.5 mb-3 text-sm border rounded-md border-amber-300 bg-amber-50 text-amber-900"
    >
      <div className="flex items-center gap-2">
        <IconAlertTriangle size={18} className="shrink-0 text-amber-500" />
        <span>
          {lang.get('msg.translationsIncomplete')}: {entry.translated} / {entry.total}
        </span>
      </div>
      <button
        type="button"
        onClick={handleRetry}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-amber-400 bg-white hover:bg-amber-100 transition-colors"
      >
        <IconRefresh size={14} />
        {lang.get('msg.retryMissingTranslations')}
      </button>
    </div>
  );
}

export default TranslationCoverageBanner;
